import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Zap, CheckCircle2, Clock } from 'lucide-react';

/**
 * Section de gestion des coupons digitaux.
 *
 * Props :
 *  - existingCoupons   : coupons déjà enregistrés en base (mode édition)
 *  - newCouponRows     : tableau de { id, code } pour les lignes en attente d'ajout
 *  - onNewCouponChange : (id, value) => void — mise à jour d'une ligne
 *  - onAddRow          : () => void — ajoute une nouvelle ligne vide
 *  - onRemoveRow       : (id) => void — retire une ligne non sauvegardée
 *  - onDeleteExisting  : (couponId) => void — supprime un coupon déjà enregistré
 *  - isDeleting        : Set<string> des couponId en cours de suppression
 */
export default function DigitalCouponsSection({
  existingCoupons = [],
  newCouponRows = [],
  onNewCouponChange,
  onAddRow,
  onRemoveRow,
  onDeleteExisting,
  isDeleting = new Set(),
}) {
  const available = existingCoupons.filter(c => !c.isUsed).length;
  const sold = existingCoupons.filter(c => c.isUsed).length;

  const inputCls =
    'flex-1 text-ink px-3 py-2 rounded-lg text-sm outline-none focus:ring-1 focus:ring-accent-green/40 transition-all';
  const inputStyle = { background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card p-5 space-y-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-ink font-medium text-sm">
          <Zap className="text-yellow-400" size={16} />
          Gestion des Coupons
        </label>
        {existingCoupons.length > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-0.5 rounded-full font-medium"
              style={{ background: 'rgba(79,179,103,0.15)', color: 'var(--c-accent-green)' }}>
              {available} disponible{available !== 1 ? 's' : ''}
            </span>
            {sold > 0 && (
              <span className="px-2 py-0.5 rounded-full font-medium text-green-400"
                style={{ background: 'rgba(34,197,94,0.12)' }}>
                {sold} vendu{sold !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Coupons existants (mode édition) */}
      {existingCoupons.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-label font-medium uppercase tracking-wider">Coupons enregistrés</p>
          <AnimatePresence>
            {existingCoupons.map(coupon => (
              <motion.div
                key={coupon._id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8, height: 0 }}
                className="flex items-center gap-2"
              >
                <div
                  className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg ${coupon.isUsed ? 'opacity-60' : ''}`}
                  style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                >
                  {coupon.isUsed ? (
                    <CheckCircle2 size={14} className="text-green-400 flex-shrink-0" />
                  ) : (
                    <Clock size={14} className="text-label flex-shrink-0" />
                  )}
                  <span
                    className={`flex-1 font-mono text-sm ${coupon.isUsed ? 'line-through text-label' : 'text-ink'}`}
                  >
                    {coupon.couponCode}
                  </span>
                  {coupon.isUsed ? (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium text-green-400"
                      style={{ background: 'rgba(34,197,94,0.12)' }}>
                      Vendu
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: 'rgba(79,179,103,0.12)', color: 'var(--c-accent-green)' }}>
                      Disponible
                    </span>
                  )}
                </div>
                {!coupon.isUsed && onDeleteExisting && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => onDeleteExisting(coupon._id)}
                    disabled={isDeleting.has(coupon._id)}
                    className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
                  >
                    {isDeleting.has(coupon._id) ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </motion.button>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Nouvelles lignes */}
      {newCouponRows.length > 0 && (
        <div className="space-y-2">
          {existingCoupons.length > 0 && (
            <p className="text-xs text-label font-medium uppercase tracking-wider">Nouveaux codes à ajouter</p>
          )}
          <AnimatePresence>
            {newCouponRows.map((row, index) => (
              <motion.div
                key={row.id}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6, height: 0 }}
                className="flex items-center gap-2"
              >
                <span className="text-label text-xs w-5 text-right flex-shrink-0">
                  {existingCoupons.length + index + 1}.
                </span>
                <input
                  type="text"
                  value={row.code}
                  onChange={e => onNewCouponChange(row.id, e.target.value)}
                  placeholder="Ex: WIFI-ZONE-2024-ABCD"
                  className={inputCls}
                  style={inputStyle}
                  autoFocus={index === newCouponRows.length - 1}
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={() => onRemoveRow(row.id)}
                  className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 size={14} />
                </motion.button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Bouton Ajouter une ligne */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        type="button"
        onClick={onAddRow}
        className="w-full py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all"
        style={{
          border: '1px dashed var(--c-border)',
          color: 'var(--c-accent-green)',
          background: 'rgba(var(--c-accent-green-rgb), 0.04)',
        }}
      >
        <Plus size={15} />
        Ajouter un coupon
      </motion.button>

      {newCouponRows.length > 0 && (
        <p className="text-xs text-label">
          {newCouponRows.filter(r => r.code.trim()).length} code(s) seront enregistrés à la sauvegarde.
        </p>
      )}
    </motion.div>
  );
}
